import type { AttendanceDashboardItem } from '@src/models/interfaces/attendanceDashboard.interface';
import type { IWorkJourneyConfigStaffingSummary } from '@src/models/interfaces/workJourneyManagement.interface';
import { JOURNEY_ATTENDANCE_STATUS } from '@src/packages/workJourney/constants';

import {
  isJourneyBelowMinimum,
  isUserAbsent,
  isUserNotMarked,
  isUserPresent,
  resolveJourneyAttendanceStatus,
} from './attendanceStatus/resolveAttendanceStatus';
import type {
  AttendanceKpis,
  JourneyAttendanceGroupRow,
  JourneyAttendanceStatusInput,
  UserAttendanceStatusInput,
} from './attendanceStatus/types';
import { timestampSecondsOrNull } from './buildCurrentDayRange';

/** True when the dashboard row already has a shift-start mark. */
export const hasAttendanceJourneyStarted = (
  item: Pick<AttendanceDashboardItem, 'actualStartAt'>,
): boolean => timestampSecondsOrNull(item.actualStartAt) !== null;

/** Map key for null scheduled starts (nested Map cannot use null as a key). */
const UNSCHEDULED_START_KEY = 'unscheduled' as const;
type JourneyStartKey = number | typeof UNSCHEDULED_START_KEY;
export type JourneyStaffingByConfigId = ReadonlyMap<
  string,
  IWorkJourneyConfigStaffingSummary
>;

const toUserStatusInput = (item: AttendanceDashboardItem): UserAttendanceStatusInput => ({
  scheduledStartAtSeconds: timestampSecondsOrNull(item.scheduledStartAt),
  scheduledEndAtSeconds: timestampSecondsOrNull(item.scheduledEndAt),
  actualStartAtSeconds: timestampSecondsOrNull(item.actualStartAt),
  actualEndAtSeconds: timestampSecondsOrNull(item.actualEndAt),
  toleranceTimeMinutes: item.toleranceTimeMinutes,
  leaveType: item.leaveType,
  observations: item.observations,
});

const buildJourneyStatusInput = (
  records: AttendanceDashboardItem[],
  staffingByConfigId?: JourneyStaffingByConfigId,
): JourneyAttendanceStatusInput => {
  const first = records[0];
  const staffing = first?.workJourneyConfigId
    ? staffingByConfigId?.get(first.workJourneyConfigId)
    : undefined;
  const markedCount = records.filter(hasAttendanceJourneyStarted).length;

  return {
    scheduledStartAtSeconds: timestampSecondsOrNull(first?.scheduledStartAt),
    scheduledEndAtSeconds: timestampSecondsOrNull(first?.scheduledEndAt),
    toleranceTimeMinutes: first?.toleranceTimeMinutes ?? 0,
    requiredMembers: staffing?.requiredMembers ?? first?.requiredMembers ?? 0,
    // Elastic row count until live roster staffing is in the map (0 is a valid roster value).
    assignedCount: staffing?.assignedMembers ?? records.length,
    markedCount,
  };
};

const toStartKey = (scheduledStartAtSeconds: number | null): JourneyStartKey =>
  scheduledStartAtSeconds === null ? UNSCHEDULED_START_KEY : scheduledStartAtSeconds;

const fromStartKey = (startKey: JourneyStartKey): number | null =>
  startKey === UNSCHEDULED_START_KEY ? null : startKey;

/** Opaque instance id for table keys / sync maps — do not parse; use structured fields instead. */
export const buildJourneyInstanceId = (
  workJourneyConfigId: string,
  scheduledStartAtSeconds: number | null,
): string =>
  JSON.stringify({
    workJourneyConfigId,
    scheduledStartAtSeconds,
  });

export const matchesJourneyInstance = (
  item: AttendanceDashboardItem,
  row: Pick<JourneyAttendanceGroupRow, 'workJourneyConfigId' | 'scheduledStartAtSeconds'>,
): boolean =>
  item.workJourneyConfigId === row.workJourneyConfigId &&
  timestampSecondsOrNull(item.scheduledStartAt) === row.scheduledStartAtSeconds;

/** All assigned members for a journey instance (marked and unmarked). */
export const getJourneyInstanceMembers = (
  items: AttendanceDashboardItem[],
  row: Pick<JourneyAttendanceGroupRow, 'workJourneyConfigId' | 'scheduledStartAtSeconds'>,
): AttendanceDashboardItem[] => items.filter(item => matchesJourneyInstance(item, row));

/** Members counted by the "Marked / min." journey column. */
export const getMarkedJourneyMembers = (
  items: AttendanceDashboardItem[],
  row: Pick<JourneyAttendanceGroupRow, 'workJourneyConfigId' | 'scheduledStartAtSeconds'>,
): AttendanceDashboardItem[] =>
  getJourneyInstanceMembers(items, row).filter(hasAttendanceJourneyStarted);

/** Split journey members into marked vs unmarked for the roster drawer. */
export const partitionJourneyMembersByMarking = (
  items: AttendanceDashboardItem[],
): { marked: AttendanceDashboardItem[]; unmarked: AttendanceDashboardItem[] } => {
  const marked: AttendanceDashboardItem[] = [];
  const unmarked: AttendanceDashboardItem[] = [];

  items.forEach(item => {
    if (hasAttendanceJourneyStarted(item)) {
      marked.push(item);
    } else {
      unmarked.push(item);
    }
  });

  return { marked, unmarked };
};

export const computeAttendanceKpis = (
  items: AttendanceDashboardItem[],
  staffingByConfigId?: JourneyStaffingByConfigId,
): AttendanceKpis => {
  let presentUsers = 0;
  let notMarkedUsers = 0;
  let absentUsers = 0;

  const journeyGroups = groupAttendanceByJourney(items);
  let journeysInProgress = 0;
  let requiresAction = 0;

  items.forEach(item => {
    const statusInput = toUserStatusInput(item);
    // Keep KPI buckets aligned with PRESENT / UNMARKED / ABSENT list filters.
    if (isUserPresent(statusInput)) {
      presentUsers += 1;
    } else if (isUserAbsent(statusInput)) {
      absentUsers += 1;
    } else if (isUserNotMarked(statusInput)) {
      notMarkedUsers += 1;
    }
  });

  journeyGroups.forEach(group => {
    const journeyInput = buildJourneyStatusInput(group.records, staffingByConfigId);
    const status = resolveJourneyAttendanceStatus(journeyInput);

    if (status.code === JOURNEY_ATTENDANCE_STATUS.IN_PROGRESS) {
      journeysInProgress += 1;
    }

    if (isJourneyBelowMinimum(journeyInput)) {
      requiresAction += 1;
    }
  });

  return {
    journeysInProgress,
    presentUsers,
    notMarkedUsers,
    absentUsers,
    requiresAction,
  };
};

export interface JourneyGroup {
  workJourneyConfigId: string;
  scheduledStartAtSeconds: number | null;
  records: AttendanceDashboardItem[];
}

/**
 * Groups attendance records by scheduled journey instance:
 * workJourneyConfigId + scheduledStartAt (not config alone).
 */
export const groupAttendanceByJourney = (items: AttendanceDashboardItem[]): JourneyGroup[] => {
  const groups = new Map<string, Map<JourneyStartKey, AttendanceDashboardItem[]>>();

  items.forEach(item => {
    const configId = item.workJourneyConfigId;
    if (!configId) {
      return;
    }

    const startKey = toStartKey(timestampSecondsOrNull(item.scheduledStartAt));
    let byStart = groups.get(configId);
    if (!byStart) {
      byStart = new Map();
      groups.set(configId, byStart);
    }

    const current = byStart.get(startKey) ?? [];
    current.push(item);
    byStart.set(startKey, current);
  });

  const result: JourneyGroup[] = [];
  groups.forEach((byStart, workJourneyConfigId) => {
    byStart.forEach((records, startKey) => {
      result.push({
        workJourneyConfigId,
        scheduledStartAtSeconds: fromStartKey(startKey),
        records,
      });
    });
  });

  return result;
};

export const buildJourneyAttendanceRows = (
  items: AttendanceDashboardItem[],
  staffingByConfigId?: JourneyStaffingByConfigId,
): JourneyAttendanceGroupRow[] =>
  groupAttendanceByJourney(items).map(group => {
    const first = group.records[0];
    const journeyInput = buildJourneyStatusInput(group.records, staffingByConfigId);

    return {
      id: buildJourneyInstanceId(group.workJourneyConfigId, group.scheduledStartAtSeconds),
      workJourneyConfigId: group.workJourneyConfigId,
      scheduledStartAtSeconds: group.scheduledStartAtSeconds,
      journeyName: first?.journeyName ?? '—',
      regulationName: first?.regulationName ?? null,
      requiredMembers: journeyInput.requiredMembers,
      assignedCount: journeyInput.assignedCount,
      markedCount: journeyInput.markedCount,
      status: resolveJourneyAttendanceStatus(journeyInput),
    };
  });

export { toUserStatusInput };
